import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Download, Loader2, Printer, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, downloadFile, errMsg } from "@/lib/api";
import { Markdown, PageHeader } from "@/components/Bits";
import { RootsLine } from "@/components/Roots";
import { FinishFoundationsCard } from "@/components/RootsExtras";
import { RootsFoundationsForm } from "@/components/RootsFoundationsForm";
import { RootsTabs } from "@/components/RootsTabs";
import { ShareProfile } from "@/components/ShareProfile";

const AUD = { cpg: "CPG food & drink founder", hospitality: "Independent hospitality", both: "CPG brand and hospitality" };

export default function Foundations() {
  const [d, setD] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const getPdf = async () => {
    setPdfBusy(true);
    try { await downloadFile("/me/roots-profile/pdf", "My-Roots-Profile.pdf"); } catch (e) { toast.error(errMsg(e)); } finally { setPdfBusy(false); }
  };
  useEffect(() => {
    api.get("/me/foundations").then((r) => setD((x) => x || r.data));
    api.post("/me/foundations/viewed").catch(() => {});
  }, []);
  useEffect(() => {
    if (d && window.location.hash) setTimeout(() => document.querySelector(window.location.hash)?.scrollIntoView({ behavior: "smooth" }), 400);
  }, [d]);
  if (!d) return null;
  const togglePartner = async (v) => {
    setD((x) => ({ ...x, open_to_partnerships: v }));
    try {
      await api.put("/me/partnerships", { open: v });
      toast.success(v ? "Lindsay will know you're open to partnerships" : "Updated");
    } catch (e) {
      setD((x) => ({ ...x, open_to_partnerships: !v }));
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="max-w-3xl">
      <PageHeader eyebrow={AUD[d.audience] || "Your profile"} title="Your Roots profile." />
      <RootsTabs />
      <FinishFoundationsCard to="#marketing-foundations" />
      {d.growth_phase && (
        <div className="rounded-2xl bg-navy text-white p-6 mb-6" data-testid="foundations-phase">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-sun">Your Roots Growth Phase</div>
          <div className="font-serif uppercase text-3xl mt-1">Phase {d.growth_phase}: {d.phase_name}</div>
          <p className="text-sm text-white/85 mt-2">{d.phase_summary}</p>
          <RootsLine className="w-56 h-8 text-sun/40 mt-3" />
        </div>
      )}
      {d.focus?.length > 0 && (
        <div className="rounded-2xl border-2 border-pinkb/30 bg-card p-6 mb-6" data-testid="foundations-focus">
          <h2 className="font-serif uppercase text-xl text-navy mb-3">Your 90-day focus</h2>
          <ol className="space-y-3">{d.focus.map((f, i) => (
            <li key={f.action} className="flex flex-wrap items-center justify-between gap-3 text-sm" data-testid={`focus-${i}`}>
              <span><span className="font-serif text-pinkb mr-2">0{i + 1}</span>{f.action}</span>
              {f.resource_id ? <Link to={`/app/library?open=${f.resource_id}`} data-testid={`focus-link-${i}`} className="inline-flex items-center gap-1 text-pinkb hover:underline">{f.resource_title} <ArrowRight className="h-3 w-3" /></Link>
                : <span className="text-xs rounded-full bg-secondary px-3 py-1 text-muted-foreground" data-testid={`focus-soon-${i}`}>Template coming soon</span>}
            </li>
          ))}</ol>
        </div>
      )}
      <div className="rounded-2xl border border-border bg-card p-6 lg:p-8"><Markdown testId="foundations-content">{d.foundations || "You haven't completed your scorecard yet."}</Markdown></div>
      <section id="marketing-foundations" className="mt-6 scroll-mt-6 rounded-2xl border-2 border-pinkb/30 bg-card p-6" data-testid="foundations-marketing">
        <h2 className="font-serif uppercase text-xl text-navy mb-3">Your marketing foundations</h2>
        <RootsFoundationsForm audience={d.audience} />
      </section>
      <div className="mt-6 rounded-2xl bg-secondary p-6" data-testid="phase-guide">
        <h2 className="font-serif uppercase text-xl text-navy mb-4">The Roots Growth Model: all five phases</h2>
        <ol className="space-y-4">{Object.entries(d.phases || {}).map(([k, p]) => (
          <li key={k} className={`rounded-xl p-4 ${Number(k) === d.growth_phase ? "bg-white border-2 border-pinkb" : "bg-white/60"}`} data-testid={`phase-${k}`}>
            <div className="font-serif uppercase text-navy">Phase {k}: {p.name}{Number(k) === d.growth_phase && <span className="ml-2 text-xs text-pinkb normal-case font-sans">You are here</span>}</div>
            <p className="text-sm text-muted-foreground mt-1">{p.summary}</p>
          </li>
        ))}</ol>
      </div>
      <label className="mt-6 flex items-start gap-3 rounded-2xl border border-border bg-card p-5 text-sm cursor-pointer" data-testid="partnerships-toggle">
        <input type="checkbox" checked={d.open_to_partnerships} onChange={(e) => togglePartner(e.target.checked)} data-testid="partnerships-checkbox" className="mt-1" />
        <span><span className="font-medium text-navy">I'm open to partnerships and collaborations with other members.</span><br /><span className="text-muted-foreground">Lindsay will make introductions when there's a good fit.</span></span>
      </label>
      <ShareProfile />
      <div className="mt-6 flex flex-wrap gap-3 text-sm">
        <button onClick={getPdf} disabled={pdfBusy} data-testid="foundations-pdf" className="inline-flex items-center gap-2 rounded-full bg-navy text-white px-4 py-2 hover:bg-navy/90 disabled:opacity-60">{pdfBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}Download my Roots Profile PDF</button>
        <button onClick={() => window.print()} data-testid="foundations-print" className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 hover:border-pinkb"><Printer className="h-4 w-4" />Print or save as PDF</button>
        <Link to="/onboarding" data-testid="foundations-retake" className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 hover:border-pinkb"><RefreshCw className="h-4 w-4" />Retake the scorecard</Link>
      </div>
    </div>
  );
}
